import { HttpClient, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilesService } from '@brafton/feedback-api';

// A presigned single PUT to S3 caps an object at 5 GB; anything larger needs a
// multipart upload, which this flow doesn't implement.
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024 * 1024;

type UploadStatus = 'preparing' | 'ready' | 'uploading' | 'done' | 'error';

/** One file queued for upload, with its signed URL and live progress. */
interface UploadEntry {
  id: number;
  file: File;
  name: string;
  size: number;
  /** Object URL for an image preview thumbnail (null for non-images). */
  previewUrl: string | null;
  /** Signed PUT URL fetched when the file was added. */
  signedUrl: string | null;
  /** The stored object URL (signed URL without its query). */
  objectUrl: string | null;
  status: UploadStatus;
  progress: number;
  error: string | null;
}

/**
 * Slide-over drawer for uploading multiple mockup files to a feedback round.
 * When files are added it pre-fetches a signed upload URL for each; on "Start
 * Upload" it PUTs every file to its signed URL with live per-file progress.
 * Emits `uploaded` with the stored object URLs of the files that succeeded; the
 * host persists them to the round (e.g. via `addMockup`).
 */
@Component({
  selector: 'app-upload-mockups-drawer',
  templateUrl: './upload-mockups-drawer.html',
  styleUrl: './upload-mockups-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onClose()' },
})
export class UploadMockupsDrawer {
  /** Whether the drawer is shown. */
  readonly open = input<boolean>(false);

  /** The round the mockups belong to (used to namespace the signed-URL key). */
  readonly feedbackRoundId = input<string>('');

  /** Emitted with the object URLs of the files that uploaded successfully. */
  readonly uploaded = output<string[]>();

  /** Emitted when the user closes the drawer (button, overlay, or Escape). */
  readonly close = output<void>();

  private readonly files = inject(FilesService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entries = signal<UploadEntry[]>([]);
  protected readonly uploading = signal(false);
  protected readonly dragOver = signal(false);

  private nextId = 0;

  /** A new upload can start once at least one file is ready and none are in flight. */
  protected readonly canStart = computed(
    () => !this.uploading() && this.entries().some((entry) => entry.status === 'ready'),
  );

  protected readonly totalSize = computed(() =>
    this.entries().reduce((sum, entry) => sum + entry.size, 0),
  );

  constructor() {
    // Clear the queue (and revoke previews) whenever the drawer closes.
    effect(() => {
      if (!this.open()) {
        untracked(() => this.reset());
      }
    });
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = '';
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    this.addFiles(event.dataTransfer?.files ?? null);
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(true);
  }

  protected onDragLeave(): void {
    this.dragOver.set(false);
  }

  /** Queues each file and pre-fetches a signed upload URL for it. */
  private addFiles(fileList: FileList | null): void {
    if (!fileList?.length) {
      return;
    }
    const roundId = this.feedbackRoundId();
    for (const file of Array.from(fileList)) {
      const id = this.nextId++;
      const contentType = file.type || 'application/octet-stream';
      const tooLarge = file.size > MAX_UPLOAD_BYTES;
      const entry: UploadEntry = {
        id,
        file,
        name: file.name,
        size: file.size,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        signedUrl: null,
        objectUrl: null,
        status: tooLarge ? 'error' : 'preparing',
        progress: 0,
        error: tooLarge ? 'Exceeds the 5 GB upload limit.' : null,
      };
      this.entries.update((current) => [...current, entry]);

      // Don't request an upload URL for a file S3 can't accept in a single PUT.
      if (tooLarge) {
        continue;
      }

      const filename = `${Date.now()}-${id}-${file.name.replace(/[^\w.\-]+/g, '_')}`;
      this.files
        .getSignedUrl({ feedbackRoundId: roundId, filename, contentType })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (signedUrl) =>
            this.patch(id, { signedUrl, objectUrl: signedUrl.split('?')[0], status: 'ready' }),
          error: () => this.patch(id, { status: 'error', error: 'Could not prepare upload.' }),
        });
    }
  }

  protected removeEntry(id: number): void {
    const entry = this.entries().find((candidate) => candidate.id === id);
    if (entry?.previewUrl) {
      URL.revokeObjectURL(entry.previewUrl);
    }
    this.entries.update((current) => current.filter((candidate) => candidate.id !== id));
  }

  /** PUTs every ready file to its signed URL, tracking per-file progress. */
  protected startUploads(): void {
    if (!this.canStart()) {
      return;
    }
    this.uploading.set(true);
    const ready = this.entries().filter((entry) => entry.status === 'ready' && entry.signedUrl);
    let remaining = ready.length;

    const settle = (): void => {
      remaining -= 1;
      if (remaining === 0) {
        this.finishUploads();
      }
    };

    for (const entry of ready) {
      this.patch(entry.id, { status: 'uploading', progress: 0, error: null });
      this.http
        .put(entry.signedUrl as string, entry.file, {
          headers: { 'Content-Type': entry.file.type || 'application/octet-stream' },
          reportProgress: true,
          observe: 'events',
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              this.patch(entry.id, { progress: Math.round((event.loaded / event.total) * 100) });
            } else if (event.type === HttpEventType.Response) {
              this.patch(entry.id, { status: 'done', progress: 100 });
            }
          },
          error: () => {
            this.patch(entry.id, { status: 'error', error: 'Upload failed.' });
            settle();
          },
          complete: () => settle(),
        });
    }
  }

  private finishUploads(): void {
    this.uploading.set(false);
    const doneUrls = this.entries()
      .filter((entry) => entry.status === 'done' && entry.objectUrl)
      .map((entry) => entry.objectUrl as string);
    if (doneUrls.length) {
      this.uploaded.emit(doneUrls);
    }
    // Close automatically only when everything succeeded; otherwise stay open so
    // the failed rows are visible.
    if (!this.entries().some((entry) => entry.status === 'error')) {
      this.close.emit();
    }
  }

  protected onClose(): void {
    if (!this.uploading()) {
      this.close.emit();
    }
  }

  protected formatSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    if (bytes < 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  /** Patches a single entry by id (immutably, so the signal updates). */
  private patch(id: number, changes: Partial<UploadEntry>): void {
    this.entries.update((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)),
    );
  }

  private reset(): void {
    for (const entry of this.entries()) {
      if (entry.previewUrl) {
        URL.revokeObjectURL(entry.previewUrl);
      }
    }
    this.entries.set([]);
    this.uploading.set(false);
    this.dragOver.set(false);
    this.nextId = 0;
  }
}
